import type { StatusAlias } from '../components/StatusPill';

export type FlowType = 'platform' | 'pharmacy' | 'shipping' | 'dr_network' | 'manual';

export const FLOW_META: Record<FlowType, { label: string; color: string; rails: string; cadence: string }> = {
  platform: { label: 'Platform', color: 'var(--c-blue-500)', rails: 'Stripe', cadence: 'Monthly · 1st' },
  pharmacy: { label: 'Pharmacy', color: 'var(--c-emerald-500)', rails: 'Hyperswitch', cadence: 'Weekly · Fri' },
  shipping: { label: 'Shipping', color: 'var(--c-purple-500)', rails: 'Hyperswitch', cadence: '~Monthly' },
  dr_network: { label: 'Dr network', color: 'var(--c-amber-500)', rails: 'Wire / ACH', cadence: 'Monthly · 1st' },
  manual: { label: 'Manual', color: 'var(--p-muted-fg)', rails: '—', cadence: 'Ad-hoc' },
};

export type PayableStatus = 'draft' | 'open' | 'paid' | 'void';

export const PAYABLE_STATUS: Record<PayableStatus, { tone: StatusAlias; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  open: { tone: 'in-progress', label: 'Owed' },
  paid: { tone: 'success', label: 'Paid' },
  void: { tone: 'neutral', label: 'Void' },
};

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'past_due' | 'partial' | 'failed' | 'void';

/**
 * What a void did with the usage the invoice claimed: 'rebill' released it to
 * be billed again, 'cancel' kept it claimed so it never is. Absent on older
 * voids, which were all rebills.
 */
export type VoidKind = 'rebill' | 'cancel';

export const INVOICE_STATUS: Record<InvoiceStatus, { tone: StatusAlias; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  open: { tone: 'in-progress', label: 'Open' },
  paid: { tone: 'success', label: 'Paid' },
  past_due: { tone: 'attention', label: 'Past Due' },
  failed: { tone: 'error', label: 'Failed' },
  partial: { tone: 'partial', label: 'Partial' },
  void: { tone: 'neutral', label: 'Void' },
};

/**
 * How an invoice's state reads on screen. Cancel is stored as a void (nothing
 * downstream has to learn a new status) but is a different act, so it says so.
 */
export const invoiceStatusBadge = (status: InvoiceStatus, voidKind?: VoidKind) =>
  status === 'void' && voidKind === 'cancel'
    ? { tone: INVOICE_STATUS.void.tone, label: 'Cancelled' }
    : INVOICE_STATUS[status];
